import { useRef, useState } from "react";
import { SPEED_UNITS } from "../admin-details/account/constants";
import { DEFAULT_REPLAY_SPEED } from "../map-asset/multiDay/ReplayControlsPanel";
import { replayTrips } from "../map-asset/multiDay/replayTrips";
import { createTooltipController } from "../map-asset/multiDay/tooltip";
import {
  statusCodeIsPinned,
  StatusCodeFilter,
} from "../map-asset/statusCodeFilter";
import { TMapRef } from "../map-asset/type";
import { VehicleGroup } from "./useMultiVehicleTripData";
import {
  tripDisplayNumberOr,
  tripDisplayNumbers,
} from "../map-asset/multiDay/tripNumbering";

export const EVENT_REPLAY_SPEED_OPTIONS = [1, 2, 5, 10];

// how long the finished replay stays up before the routes come back
const END_HOLD_MS = 1500;

// the event-by-event replay behind "Replay Events" in a trip's detail. this
// is the ride-along view - distinct from the map's shared-clock animation,
// which draws whole routes for every selected vehicle at once.
export const useVehicleEventReplay = ({
  mapRef,
  vehicleGroups,
  speedUnit,
  filteredStatusCodes,
}: {
  mapRef: React.MutableRefObject<TMapRef>;
  vehicleGroups: VehicleGroup[];
  // the account's configured speed unit, for the per-event hover tooltip
  speedUnit?: SPEED_UNITS;
  // the Event Filter's current selection - the replay pins only the events
  // it is showing, so it can't put filtered-out pins back on the map
  filteredStatusCodes?: StatusCodeFilter;
}) => {
  const [replayingTripKey, setReplayingTripKey] = useState<string | null>(null);
  const [replayPercentage, setReplayPercentage] = useState<number | null>(null);
  // the reported time of the event just revealed - the universal clock for
  // this replay. exact, since it walks real reported fixes one at a time.
  const [replayClock, setReplayClock] = useState<number | null>(null);
  // the asset's reported speed at the fix on the clock - the timestamp on
  // its own invites the question "and how fast were they going?"
  const [replaySpeedKph, setReplaySpeedKph] = useState<number | null>(null);
  const [replaySpeed, setReplaySpeed] = useState<number>(DEFAULT_REPLAY_SPEED);
  const replaySpeedRef = useRef(replaySpeed);
  replaySpeedRef.current = replaySpeed;
  // the ref as well as the state: a replay starting in this same tick reads
  // the ref before React has re-rendered
  const resetSpeed = () => {
    replaySpeedRef.current = DEFAULT_REPLAY_SPEED;
    setReplaySpeed(DEFAULT_REPLAY_SPEED);
  };
  const activeRef = useRef<{
    stop: () => void;
    pause: () => void;
    resume: () => void;
  } | null>(null);
  const tooltipRef = useRef<ReturnType<typeof createTooltipController> | null>(
    null
  );
  const [isPaused, setIsPaused] = useState(false);
  const filteredStatusCodesRef = useRef(filteredStatusCodes);
  filteredStatusCodesRef.current = filteredStatusCodes;

  const stopReplay = () => {
    activeRef.current?.stop();
    activeRef.current = null;
    tooltipRef.current?.destroy();
    tooltipRef.current = null;
    setReplayingTripKey(null);
    setReplayPercentage(null);
    setReplayClock(null);
    setReplaySpeedKph(null);
    setIsPaused(false);
    resetSpeed();
  };

  const togglePause = () => {
    if (!activeRef.current) return;
    if (isPaused) activeRef.current.resume();
    else activeRef.current.pause();
    setIsPaused((paused) => !paused);
  };

  const startReplay = (tripKey: string) => {
    const map = mapRef.current?.map;
    if (!map) return;
    stopReplay();

    const [deviceId, tripIndexKey] = tripKey.split("__");
    const group = vehicleGroups.find((item) => item.deviceId === deviceId);
    const points = group?.trips[Number(tripIndexKey)];
    if (!group || !points || points.length === 0) return;

    const tooltip = createTooltipController();
    tooltipRef.current = tooltip;
    setReplayingTripKey(tripKey);
    setReplayPercentage(0);
    setReplayClock(points[0]?.timestamp ?? null);
    resetSpeed();

    const handle = replayTrips({
      map,
      trips: [
        {
          points,
          color: group.color,
          label: `${group.fullLabel} · Trip ${tripDisplayNumberOr(
            tripDisplayNumbers(group.trips),
            Number(tripIndexKey)
          )}`,
        },
      ],
      tooltip,
      isPinned: (point) =>
        statusCodeIsPinned(filteredStatusCodesRef.current, point.statuscode),
      getSpeedMultiplier: () => replaySpeedRef.current,
      speedUnit,
      onProgress: (percent, clock, speedKph) => {
        setReplayPercentage(percent);
        if (clock !== undefined) setReplayClock(clock);
        setReplaySpeedKph(speedKph ?? null);
      },
      // clearing only the state here left the replay's own markers on the
      // map, under the routes coming back - so this tears it down properly,
      // after a moment on the finished picture
      onDone: () => {
        setTimeout(() => {
          if (activeRef.current === handle) stopReplay();
        }, END_HOLD_MS);
      },
    });
    activeRef.current = handle;
  };

  return {
    replayingTripKey,
    replayPercentage,
    replayClock,
    replaySpeedKph,
    replaySpeed,
    setReplaySpeed,
    startReplay,
    stopReplay,
    togglePause,
    isPaused,
    isReplaying: replayingTripKey !== null,
  };
};
